import { useEffect, useState } from "react";
import API from "../api/axios";
import Layout from "../components/Layout";
import toast from "react-hot-toast";
import { useTheme } from "../context/ThemeContext";

export default function Debts() {
  const { theme } = useTheme();
  const [debts, setDebts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("outstanding");
  const [payModal, setPayModal] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [paying, setPaying] = useState(false);

  const card = {
    background: theme.cardBg,
    border: `1px solid ${theme.cardBorder}`,
    borderRadius: "14px",
    padding: "20px",
    boxShadow: theme.cardShadow,
  };
  const input = {
    background: theme.inputBg,
    border: `1px solid ${theme.inputBorder}`,
    borderRadius: "10px",
    padding: "12px 16px",
    color: theme.textPrimary,
    fontSize: "14px",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  };

  const fetchDebts = async () => {
    setLoading(true);
    try {
      const [debtsRes, summaryRes] = await Promise.all([
        API.get("/debts/"),
        API.get("/debts/summary"),
      ]);
      setDebts(debtsRes.data);
      setSummary(summaryRes.data);
    } catch {
      toast.error("Failed to load debts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDebts(); }, []);

  const handlePayment = async () => {
  if (!payAmount || parseFloat(payAmount) <= 0) {
    toast.error("Enter a valid amount");
    return;
  }
  setPaying(true);
  try {
    const res = await API.patch(`/debts/${payModal.id}/pay`, {
      amount_paid: parseFloat(payAmount),
    });

    // Show different message based on whether fully settled or partial
    if (res.data.status === "settled") {
      toast.success("Debt fully settled — revenue updated");
    } else {
      toast.success(`Payment of ₦${parseFloat(payAmount).toLocaleString()} recorded — balance remaining`);
    }

    setPayModal(null);
    setPayAmount("");
    fetchDebts();
  } catch (err) {
    toast.error(err.response?.data?.detail || "Failed to record payment");
  } finally {
    setPaying(false);
  }
};
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this debt record?")) return;
    try {
      await API.delete(`/debts/${id}`);
      toast.success("Debt deleted");
      fetchDebts();
    } catch {
      toast.error("Failed to delete debt");
    }
  };

  const filtered = debts.filter(d =>
    filter === "all" ? true : d.status === filter
  );

  const getDaysOverdue = (dueDate) => {
    if (!dueDate) return null;
    const diff = Math.floor((new Date() - new Date(dueDate)) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const getStatusColor = (status) => {
    if (status === "settled") return "#2ecc71";
    if (status === "partial") return "#f5a623";
    return "#e74c3c";
  };

  return (
    <Layout>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "32px" }}>
        <div>
          <h1 style={{ fontSize: "28px", fontWeight: "700", color: theme.textPrimary, letterSpacing: "-0.5px" }}>
            Debts
          </h1>
          <p style={{ fontSize: "14px", color: theme.textSecondary, marginTop: "4px" }}>
            Track credit sales and customer repayments
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "16px", marginBottom: "32px" }}>
          <div style={card}>
            <p style={{ fontSize: "13px", color: theme.textSecondary, fontWeight: "500", marginBottom: "8px" }}>
              Total Outstanding
            </p>
            <p style={{ fontSize: "22px", fontWeight: "700", color: "#e74c3c", letterSpacing: "-0.5px" }}>
              ₦{Number(summary.total_outstanding).toLocaleString()}
            </p>
          </div>
          <div style={card}>
            <p style={{ fontSize: "13px", color: theme.textSecondary, fontWeight: "500", marginBottom: "8px" }}>
              Active Debtors
            </p>
            <p style={{ fontSize: "22px", fontWeight: "700", color: "#f5a623", letterSpacing: "-0.5px" }}>
              {summary.total_debtors}
            </p>
          </div>
          <div style={card}>
            <p style={{ fontSize: "13px", color: theme.textSecondary, fontWeight: "500", marginBottom: "8px" }}>
              Settled Debts
            </p>
            <p style={{ fontSize: "22px", fontWeight: "700", color: "#2ecc71", letterSpacing: "-0.5px" }}>
              {summary.total_settled}
            </p>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div style={{
        display: "flex", background: theme.cardBg,
        border: `1px solid ${theme.cardBorder}`,
        borderRadius: "12px", padding: "4px",
        marginBottom: "24px", width: "fit-content", gap: "4px",
      }}>
        {["outstanding", "partial", "settled", "all"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: "8px 20px", borderRadius: "10px", border: "none",
            fontSize: "13px", fontWeight: "600", cursor: "pointer",
            fontFamily: "inherit", textTransform: "capitalize",
            background: filter === f ? "#2ec4b6" : "none",
            color: filter === f ? "#0a0f0f" : theme.textSecondary,
            transition: "all 0.2s",
          }}>{f}</button>
        ))}
      </div>

      {/* Debts List */}
      {loading ? (
        <p style={{ color: theme.textSecondary }}>Loading...</p>
      ) : filtered.length === 0 ? (
        <div style={{ ...card, padding: "48px 24px", textAlign: "center" }}>
          <p style={{ fontSize: "48px", marginBottom: "16px" }}>💳</p>
          <p style={{ color: theme.textSecondary }}>
            {filter === "outstanding"
              ? "No outstanding debts. All customers are paid up!"
              : `No ${filter} debts found.`}
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {filtered.map((debt) => {
            const balance = debt.amount_owed - debt.amount_paid;
            const daysOverdue = getDaysOverdue(debt.due_date);
            const isOverdue = daysOverdue > 0 && debt.status !== "settled";

            return (
              <div key={debt.id} style={{
                ...card,
                border: isOverdue
                  ? "1px solid #e74c3c55"
                  : `1px solid ${theme.cardBorder}`,
                background: isOverdue
                  ? (theme === "light" ? "#fff5f5" : "#1a0a0a")
                  : theme.cardBg,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
                      <p style={{ fontSize: "16px", fontWeight: "700", color: theme.textPrimary }}>
                        {debt.customer_name}
                      </p>
                      <span style={{
                        fontSize: "11px", fontWeight: "600", padding: "3px 10px",
                        borderRadius: "20px", textTransform: "capitalize",
                        background: debt.status === "settled" ? "#0d2e1a" : debt.status === "partial" ? "#2e1a00" : "#2e0d0d",
                        color: getStatusColor(debt.status),
                      }}>
                        {debt.status}
                      </span>
                      {isOverdue && (
                        <span style={{
                          fontSize: "11px", fontWeight: "600", padding: "3px 10px",
                          borderRadius: "20px", background: "#2e0d0d", color: "#e74c3c",
                        }}>
                          {daysOverdue}d overdue
                        </span>
                      )}
                    </div>

                    {debt.customer_phone && (
                      <p style={{ fontSize: "13px", color: theme.textSecondary, marginBottom: "4px" }}>
                        📞 {debt.customer_phone}
                      </p>
                    )}

                    <p style={{ fontSize: "13px", color: theme.textSecondary }}>
                      Sale date: {new Date(debt.sale_date || debt.created_at).toLocaleDateString("en-NG", {
                        day: "numeric", month: "short", year: "numeric",
                      })}
                    </p>

                    {debt.due_date && (
                      <p style={{ fontSize: "13px", color: isOverdue ? "#e74c3c" : theme.textSecondary }}>
                        Due: {new Date(debt.due_date).toLocaleDateString("en-NG", {
                          day: "numeric", month: "short", year: "numeric",
                        })}
                      </p>
                    )}
                  </div>

                  <div style={{ textAlign: "right" }}>
                    <p style={{ fontSize: "12px", color: theme.textSecondary, marginBottom: "4px" }}>
                      Total: ₦{Number(debt.amount_owed).toLocaleString()}
                    </p>
                    {debt.amount_paid > 0 && (
                      <p style={{ fontSize: "12px", color: "#2ecc71", marginBottom: "4px" }}>
                        Paid: ₦{Number(debt.amount_paid).toLocaleString()}
                      </p>
                    )}
                    <p style={{ fontSize: "20px", fontWeight: "800", color: debt.status === "settled" ? "#2ecc71" : "#e74c3c" }}>
                      ₦{Number(balance).toLocaleString()}
                    </p>
                    <p style={{ fontSize: "11px", color: theme.textMuted }}>
                      {debt.status === "settled" ? "fully paid" : "balance due"}
                    </p>
                  </div>
                </div>

                {/* Action buttons */}
                <div style={{ display: "flex", gap: "10px", marginTop: "16px", paddingTop: "16px", borderTop: `1px solid ${theme.divider}` }}>
                  {debt.status !== "settled" && (
                    <button onClick={() => { setPayModal(debt); setPayAmount(""); }} style={{
                      background: "linear-gradient(135deg, #2ec4b6, #1a9e92)",
                      color: "#0a0f0f", border: "none", borderRadius: "8px",
                      padding: "8px 20px", fontSize: "13px", fontWeight: "700", cursor: "pointer",
                    }}>
                      Record Payment
                    </button>
                  )}
                  {debt.customer_phone && debt.status !== "settled" && (
                    <a
                      href={`https://wa.me/${debt.customer_phone.replace(/\D/g, "")}?text=Hi ${debt.customer_name}, this is a reminder that you have an outstanding balance of ₦${Number(balance).toLocaleString()} with us. Kindly settle at your earliest convenience. Thank you.`}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        background: "#25D366", color: "#fff",
                        border: "none", borderRadius: "8px",
                        padding: "8px 20px", fontSize: "13px",
                        fontWeight: "700", cursor: "pointer",
                        textDecoration: "none", display: "inline-flex",
                        alignItems: "center", gap: "6px",
                      }}
                    >
                      💬 Remind on WhatsApp
                    </a>
                  )}
                  <button onClick={() => handleDelete(debt.id)} style={{
                    background: "none", border: `1px solid ${theme.cardBorder}`,
                    borderRadius: "8px", padding: "8px 16px",
                    fontSize: "13px", color: theme.textSecondary, cursor: "pointer",
                  }}>
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Payment Modal */}
      {payModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 500, padding: "20px",
        }}>
          <div style={{
            background: theme.cardBg, border: `1px solid ${theme.cardBorder}`,
            borderRadius: "16px", padding: "32px",
            width: "100%", maxWidth: "420px",
            boxShadow: theme.cardShadow,
          }}>
            <h3 style={{ fontSize: "18px", fontWeight: "700", color: theme.textPrimary, marginBottom: "8px" }}>
              Record Payment
            </h3>
            <p style={{ fontSize: "14px", color: theme.textSecondary, marginBottom: "24px" }}>
              {payModal.customer_name} — Balance: ₦{(payModal.amount_owed - payModal.amount_paid).toLocaleString()}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "24px" }}>
              <label style={{ fontSize: "13px", fontWeight: "500", color: theme.textSecondary }}>
                Amount Paid (₦)
              </label>
              <input
                type="number"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                placeholder="0.00"
                min="1"
                max={payModal.amount_owed - payModal.amount_paid}
                style={input}
                autoFocus
              />
              <p style={{ fontSize: "12px", color: theme.textSecondary, marginTop: "8px", fontStyle: "italic" }}>
  💡 Revenue updates when the full debt is settled.
  Partial payments are tracked here until then.
</p>
            </div>
            
            <div style={{ display: "flex", gap: "12px" }}>
              <button onClick={() => setPayModal(null)} style={{
                flex: 1, background: "none",
                border: `1px solid ${theme.cardBorder}`,
                borderRadius: "8px", padding: "12px",
                color: theme.textSecondary, fontSize: "14px", cursor: "pointer",
              }}>Cancel</button>
              <button onClick={handlePayment} disabled={paying} style={{
                flex: 1,
                background: "linear-gradient(135deg, #2ec4b6, #1a9e92)",
                border: "none", borderRadius: "8px", padding: "12px",
                color: "#0a0f0f", fontSize: "14px", fontWeight: "700", cursor: "pointer",
              }}>
                {paying ? "Saving..." : "Confirm Payment"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}